'use client';
import { motion } from 'motion/react';
import Link from 'next/link';
import { useI18n, useDirection } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, ArrowLeft } from 'lucide-react';

function PricingCard({
  plan,
  index,
  isPopular,
}: {
  plan: {
    name: string;
    description: string;
    price: string;
    agentCap?: string;
    features: string[];
    cta: string;
    popular?: string;
  };
  index: number;
  isPopular: boolean;
}) {
  const { isRTL } = useDirection();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const isEnterprise = plan.cta === 'Contact Us' || plan.cta === 'اتصل بنا';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative flex"
    >
      {/* Popular badge */}
      {isPopular && plan.popular && (
        <div className="absolute -top-3.5 start-1/2 -translate-x-1/2 z-10">
          <span className="px-4 py-1 text-xs font-semibold bg-orange-500 text-white rounded-full whitespace-nowrap shadow-sm">
            {plan.popular}
          </span>
        </div>
      )}

      <div
        className={`flex flex-col w-full p-7 rounded-2xl border transition-shadow ${
          isPopular
            ? 'border-orange-400/60 bg-card shadow-lg shadow-orange-500/10 ring-1 ring-orange-400/30'
            : 'border-border bg-card'
        }`}
      >
        {/* Plan header */}
        <div className="mb-5">
          <h3 className="text-base font-semibold text-foreground mb-1">{plan.name}</h3>
          <p className="text-sm text-muted-foreground leading-snug">{plan.description}</p>
        </div>

        {/* Price */}
        <div className="mb-2">
          <span className="text-4xl font-display font-semibold text-foreground">
            {plan.price}
          </span>
          {plan.price !== 'Custom' && plan.price !== 'مخصص' && (
            <span className="text-muted-foreground text-sm ms-1">/month</span>
          )}
        </div>

        {/* Agent cap */}
        {plan.agentCap && (
          <p className="text-xs font-medium text-muted-foreground mb-6 pb-5 border-b border-border">
            {plan.agentCap}
          </p>
        )}

        {/* CTA */}
        {/* CTA */}
        <div className="mb-7">
          {isPopular ? (
            <Button
              asChild
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium shadow-sm"
            >
              <Link href="/request-access">
                {plan.cta}
                <ArrowIcon className="w-4 h-4 ms-2" />
              </Link>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              className="w-full font-medium"
            >
              <Link href="/request-access">
                {plan.cta}
                <ArrowIcon className="w-4 h-4 ms-2" />
              </Link>
            </Button>
          )}
        </div>


        {/* Features */}
        <ul className="space-y-2.5 mt-auto">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              {feature.endsWith(':') ? (
                <span className="text-muted-foreground font-medium mt-1">{feature}</span>
              ) : (
                <>
                  <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-orange-500' : 'text-primary'}`} />
                  <span className="text-muted-foreground">{feature}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

export function Pricing() {
  const { t } = useI18n();

  const plans = [
    { ...t.pricing.plans.starter, key: 'starter' },
    { ...t.pricing.plans.growth, key: 'growth' },
    { ...t.pricing.plans.pro, key: 'pro' },
    { ...t.pricing.plans.enterprise, key: 'enterprise' },
  ];

  return (
    <section id="pricing" className="py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-primary mb-4"
          >
            {t.pricing.badge}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-display font-medium text-foreground mb-4"
          >
            {t.pricing.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground"
          >
            {t.pricing.subtitle}
          </motion.p>
        </div>

        {/* Pricing grid — 4 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto pt-4">
          {plans.map((plan, index) => (
            <PricingCard
              key={plan.key}
              plan={plan}
              index={index}
              isPopular={plan.key === 'growth'}
            />
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground/70 max-w-2xl mx-auto mt-10 leading-relaxed"
        >
          {t.pricing.footerNote}
        </motion.p>
      </div>
    </section>
  );
}
